import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { SxProps, ThemeProvider } from '@mui/material'

import {
  CreateCommentBody,
  TComment,
  TCommentThread,
  TCommentThreadData
} from 'types/GlobalComment'

import { useTranslation } from 'language/translator'
import { useCommentActions } from 'redux/actions/Comments'
import { useCommentThread } from 'redux/hooks/Comments'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useOnClickOutside } from 'utils/hooks'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Chip from 'components_new/atoms/Chip'
import Icon from 'components_new/atoms/Icon'
import Text from 'components_new/atoms/Text'
import TextField from 'components_new/atoms/TextField'

import ChatInput from 'components_new/molecules/ChatInput'
import GenericListItem from 'components_new/molecules/GenericListItem'
import OptionsMenuButton from 'components_new/molecules/OptionsMenuButton'
import SectionHeader from 'components_new/molecules/SectionHeader'
import TimeSinceLabel from 'components_new/molecules/TimeSinceLabel'
import UserAvatar from 'components_new/molecules/UserAvatar'

import { formatUsername } from 'utils/functions'
import { getTheme } from 'themes'

export interface CommentThreadProps {
  active: boolean
  analysisId?: string | null
  autoFocus?: boolean
  border?: boolean
  data?: TCommentThreadData
  disabledPadding?: boolean
  displayHeader?: boolean
  id?: string
  inputRef?: React.Ref<HTMLInputElement | HTMLTextAreaElement>
  keyMetricId?: string | null
  widgetId?: string | null
  onCreateComment: (body: CreateCommentBody) => void
  setFocused?: (value: boolean) => void
  sx?: SxProps
  variant: 'create' | 'reply'
}

const EMPTY_COMMENT_THREAD: Pick<TCommentThread, 'data' | 'comments'> = {
  comments: [],
  data: {
    datasetLabel: '',
    label: '',
    value: 0
  }
}

const DELETE_OVERLAY_SX = {
  alignItems: 'center',
  bgcolor: 'background.tooltip',
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  left: 0,
  justifyContent: 'center',
  p: 2,
  position: 'absolute',
  right: 0,
  top: 0,
  zIndex: 9999
} as const

const CommentThread = (props: CommentThreadProps) => {
  const {
    active,
    analysisId,
    autoFocus,
    border = true,
    data,
    disabledPadding,
    displayHeader = true,
    id,
    inputRef,
    keyMetricId,
    widgetId,
    onCreateComment,
    setFocused,
    sx,
    variant
  } = props

  const commentThreadFromId = useCommentThread(id ?? '')

  const commentThread:
    | TCommentThread
    | Pick<TCommentThread, 'data' | 'comments'> = id
      ? commentThreadFromId
      : data
        ? { data: data, comments: [] }
        : EMPTY_COMMENT_THREAD

  const { t } = useTranslation()

  const { account } = useAccountSettings()
  const { tryDeleteComment, tryUpdateComment } = useCommentActions()

  const rootRef = useRef<HTMLDivElement>(null)

  useOnClickOutside(
    rootRef,
    () => {
      if (setFocused) setFocused(false)
    },
    active // only listen when expanded
  )

  useEffect(() => {
    if (active) {
      rootRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: variant === 'create' ? 'start' : 'nearest'
      })
    }
  }, [active])

  const [tempValue, setTempValue] = useState<string>('')
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState<string>('')
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(
    null
  )

  const handleClear = () => {
    setTempValue('')
  }

  const handleSave = () => {
    if (variant === 'create') {
      handleCreate()
    } else {
      handleReply()
    }

    handleClear()
  }

  const handleCreate = () => {
    if (!tempValue) {
      return
    }

    onCreateComment({
      comment: tempValue,
      ...(widgetId
        ? { widget_id: widgetId }
        : analysisId
          ? { analysis_id: analysisId }
          : { key_metric_id: keyMetricId }),
      segment: data?.label ?? null,
      segment_value: data?.value != null ? String(data.value) : null
    })
  }

  const handleReply = () => {
    if (!tempValue || !id) {
      return
    }

    onCreateComment({
      comment: tempValue,
      ...(widgetId
        ? { widget_id: widgetId }
        : analysisId
          ? { analysis_id: analysisId }
          : { key_metric_id: keyMetricId }),
      parent_id: id,
      segment: commentThread.data.label ?? null,
      segment_value: commentThread.data.datasetLabel ?? null
    })
  }

  const handleEditSave = (commentId: string) => {
    if (!editValue) {
      return
    }

    tryUpdateComment(commentId, { comment: editValue })
    setEditingCommentId(null)
    setEditValue('')
  }

  const handleDeleteClick = (commentId: string) => {
    setDeletingCommentId(commentId)
    if (setFocused) setFocused(true)
  }

  const handleConfirmDelete = (commentId: string) => {
    tryDeleteComment(commentId)
    setDeletingCommentId(null)
    if (setFocused) setFocused(true)
  }

  const handleCancelDelete = () => {
    setDeletingCommentId(null)
    if (setFocused) setFocused(true)
  }

  const isRootComment = (commentId: string) => commentId === id

  const deletingRoot =
    deletingCommentId !== null && isRootComment(deletingCommentId)

  const comments = commentThread?.comments ?? []
  const rootComment = comments.find((comment) => comment.id === id)
  const replies = comments.filter((comment) => comment.id !== rootComment?.id)

  const renderComment = (
    comment: TComment,
    isReply: boolean,
    footer?: ReactNode
  ) => {
    if (comment.auto)
      return (
        <Box
          key={comment.id}
          sx={{
            display: 'flex',
            gap: 1,
            pl: 1.5
          }}
        >
          <UserAvatar
            email={
              comment.is_homepal_author
                ? undefined
                : comment.account
                  ? comment.account.email
                  : undefined
            }
            name={
              comment.is_homepal_author
                ? 'Homepal'
                : comment.account
                  ? formatUsername(comment.account)
                  : undefined
            }
            size={'xsmall'}
            sx={{ mt: '-1px' }}
            variant={comment.is_homepal_author ? 'homepal' : 'user'}
          />
          <Text color="text.secondary" variant="caption">
            {comment.is_homepal_author
              ? 'Homepal'
              : comment.account
                ? formatUsername(comment.account)
                : undefined}{' '}
            {comment.title}
            {' · '}
            <TimeSinceLabel value={comment.created_at} />
          </Text>
        </Box>
      )

    return (
      <Box
        key={comment.id}
        sx={{
          ...(isReply
            ? {
                borderBottom: '1px solid',
                borderColor: 'action.hover'
              }
            : {
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1
              }),
          pt: 1,
          pb: isReply ? 1 : 0,
          position: 'relative',
          ['&:hover']: { ['& .OptionMenuButton']: { opacity: 1 } }
        }}
      >
        {/*-- reply-level delete confirmation overlay --*/}
        {deletingCommentId === comment.id && comment.id !== id ? (
          <ThemeProvider theme={getTheme('dark')}>
            <Box sx={{ ...DELETE_OVERLAY_SX, gap: 0.5 }}>
              <Text color="text.primary" variant="subtitle2">
                {t('global', 'delete_comment')}
              </Text>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  onClick={handleCancelDelete}
                  size="small"
                  variant="outlined"
                >
                  {t('global', 'cancel')}
                </Button>
                <Button
                  onClick={() => handleConfirmDelete(comment.id)}
                  size="small"
                >
                  {t('global', 'delete')}
                </Button>
              </Box>
            </Box>
          </ThemeProvider>
        ) : null}

        <Box>
          <Box sx={{ px: 1.5 }}>
            <GenericListItem
              actions={
                account?.id === comment.account?.id ? (
                  <OptionsMenuButton
                    className="OptionMenuButton"
                    options={[
                      {
                        iconName: 'Edit',
                        onClick: () => {
                          setEditingCommentId(comment.id)
                          setEditValue(comment.title)
                          if (setFocused) setFocused(true)
                        },
                        title: t('global', 'edit')
                      },
                      {
                        color: 'error',
                        iconName: 'Delete',
                        onClick: () => handleDeleteClick(comment.id),
                        title: t('global', 'delete')
                      }
                    ]}
                    size="xsmall"
                    sx={{ opacity: 0 }}
                  />
                ) : null
              }
              body={
                comment.is_homepal_author
                  ? undefined
                  : comment.account
                    ? comment.account.email
                    : undefined
              }
              disablePadding={true}
              title={
                comment.is_homepal_author
                  ? 'Homepal'
                  : comment.account
                    ? formatUsername(comment.account)
                    : ''
              }
              titleAdornment={
                <TimeSinceLabel
                  sx={{ color: 'text.secondary', ml: 0.5 }}
                  value={comment.created_at}
                />
              }
              size="xsmall"
              variant={comment.is_homepal_author ? 'homepal' : 'user'}
            />
            <Box
              sx={{
                pl: isReply ? 3 : undefined,
                pb: !isReply ? 1 : undefined
              }}
            >
              {editingCommentId === comment.id ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    mt: 0.5
                  }}
                >
                  <TextField
                    autoFocus={true}
                    multiline={true}
                    noGutter={true}
                    onChange={(e) => setEditValue(e.target.value)}
                    size="small"
                    value={editValue}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      justifyContent: 'flex-end'
                    }}
                  >
                    <Button
                      onClick={() => {
                        setEditingCommentId(null)
                        setEditValue('')
                      }}
                      variant="text"
                    >
                      {t('global', 'cancel')}
                    </Button>
                    <Button
                      disabled={!editValue}
                      onClick={() => handleEditSave(comment.id)}
                    >
                      {t('global', 'save')}
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Text sx={{ mt: 0.25, whiteSpace: 'pre-wrap' }} variant="body1">
                  {comment.title}
                </Text>
              )}
            </Box>
          </Box>
          {footer ? (
            <Box sx={{ borderTop: '1px solid', borderColor: 'action.hover' }}>
              {footer}
            </Box>
          ) : null}
        </Box>
      </Box>
    )
  }

  return (
    <Box
      onClick={() => {
        if (setFocused) setFocused(true)
      }}
      ref={rootRef}
      sx={{
        ...(border
          ? {
              bgcolor: active ? 'background.paper' : 'action.hover',
              border: '1px solid',
              borderColor: active ? 'primary.main' : 'divider',
              borderRadius: 1
            }
          : null),
        cursor: !active && setFocused ? 'pointer' : undefined,
        ...(disabledPadding ? null : { px: 2, py: 1 }),
        position: 'relative',
        '&:hover': {
          ['& .OptionsMenuButton']: {
            color: 'primary.main'
          }
        },
        ...sx
      }}
    >
      {displayHeader ? (
        <SectionHeader
          actions={
            <Chip
              icon={<Icon name="Comment" />}
              label={commentThread?.comments.length}
              size="xsmall"
            />
          }
          body={
            <>
              {commentThread.data.datasetLabel}:{' '}
              <b>{Number(commentThread.data.value.toFixed(1))}</b>
            </>
          }
          divider={true}
          gutterBottom={true}
          size="medium"
          title={commentThread.data.label}
        />
      ) : null}

      {/*-- reply thread: root + nested footer aligned under body --*/}
      {variant === 'reply' && rootComment
        ? renderComment(
          rootComment,
          false,
          <>
            {replies.map((reply) => renderComment(reply, true))}

            <ChatInput
              multiline={true}
              onChange={setTempValue}
              onSubmit={handleSave}
              placeholder={t('global', 'reply_to_thread')}
              size="small"
              value={tempValue}
            />
          </>
        )
        : null}

      {/*-- create composer / inline reply field --*/}
      {variant === 'create' ? (
        <>
          <Box
            sx={{
              alignItems: 'flex-start',
              display: 'flex',
              gap: 1,
              mb: 1
            }}
          >
            {account ? (
              <UserAvatar
                email={account?.email}
                name={formatUsername(account)}
                size="small"
                sx={{ mt: 0.5 }}
              />
            ) : null}
            <TextField
              autoFocus={autoFocus}
              inputRef={inputRef}
              multiline={true}
              noGutter={true}
              onChange={(e) => setTempValue(e.target.value)}
              placeholder={t('global', 'comment_and_create_thread')}
              size="small"
              value={tempValue}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 1,
              justifyContent: 'flex-end'
            }}
          >
            {/* <Button onClick={handleCancel} variant="text">
              {t('global', 'cancel')}
            </Button> */}
            <Button disabled={!tempValue} onClick={handleSave}>
              {t('global', 'create')}
            </Button>
          </Box>
        </>
      ) : null}

      {/*-- thread-level delete confirmation overlay --*/}
      {deletingRoot ? (
        <ThemeProvider theme={getTheme('dark')}>
          <Box
            sx={{
              ...DELETE_OVERLAY_SX
            }}
          >
            <Text color="text.primary" variant="subtitle2">
              {t('global', 'delete_comment_thread')}
            </Text>
            <Box sx={{ color: 'common.white', display: 'flex', gap: 1 }}>
              <Button onClick={handleCancelDelete} variant="outlined">
                {t('global', 'cancel')}
              </Button>
              <Button onClick={() => handleConfirmDelete(deletingCommentId)}>
                {t('global', 'delete')}
              </Button>
            </Box>
          </Box>
        </ThemeProvider>
      ) : null}
    </Box>
  )
}

export default CommentThread
